import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { AboutReveal } from "./about-reveal";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { aboutHeroCopy } from "@/lib/siteCopy";

const HERO_IMAGE = "/About/about.png";

export function AboutHero() {
  const lang = useSiteLanguage();
  const t = aboutHeroCopy[lang];
  const sectionRef = useRef<HTMLElement>(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, reduceMotion ? 0 : 48]);
  const imageScale = useTransform(
    scrollYProgress,
    [0, 1],
    [1, reduceMotion ? 1 : 1.04]
  );

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-background pt-28 pb-20 md:pt-36 md:pb-28 lg:pt-40 lg:pb-32"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_oklch(var(--muted)_/_0.55),_transparent_60%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-32 top-24 h-72 w-72 rounded-full bg-foreground/[0.03] blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 bottom-10 h-64 w-64 rounded-full bg-foreground/[0.04] blur-3xl"
      />

      <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 sm:px-6 md:grid-cols-2 md:gap-14 lg:gap-20">
        <AboutReveal className="flex flex-col justify-center">
          <span className="mb-5 text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground md:mb-6">
            {t.eyebrow}
          </span>
          <h1 className="max-w-xl text-balance text-3xl font-bold leading-[1.08] tracking-tight text-foreground sm:text-4xl md:text-[2.75rem] lg:text-5xl">
            {t.title}
          </h1>
          <div className="mt-6 max-w-md space-y-4 text-pretty text-[15px] leading-relaxed text-muted-foreground md:mt-7 md:text-base">
            <p>{t.p1}</p>
            <p className="hidden sm:block">{t.p2}</p>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-3 md:mt-10">
            <a
              href="#achievements"
              className="group inline-flex items-center gap-3 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-all duration-300 ease-out hover:scale-[1.02] hover:opacity-90"
            >
              {t.journey}
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-foreground/15 transition-transform duration-300 ease-out group-hover:translate-x-0.5">
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.25} />
              </span>
            </a>
            <a
              href="#who-we-are"
              className="inline-flex items-center rounded-full border border-border bg-background/60 px-5 py-2.5 text-sm font-medium text-foreground backdrop-blur-sm transition-colors duration-300 hover:bg-muted"
            >
              {t.who}
            </a>
          </div>
        </AboutReveal>

        <AboutReveal delay={0.12} className="relative">
          <motion.div
            style={{ y: imageY, scale: imageScale }}
            className="relative aspect-[4/3] overflow-hidden rounded-[1.75rem] bg-muted shadow-[0_20px_50px_-20px_rgba(0,0,0,0.35)] md:rounded-[2rem] lg:aspect-[5/4]"
          >
            <img
              src={HERO_IMAGE}
              alt="DevDotCom - building software that powers progress"
              className="h-full w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 rounded-[inherit] ring-1 ring-inset ring-foreground/10" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-transparent" />
          </motion.div>
        </AboutReveal>
      </div>
    </section>
  );
}
